/**
 * Spoken turn instructions via the Web Speech API, with music ducking.
 *
 * Language safety (the "some voices are in Thai when English is selected" bug):
 * there were two causes and both are fixed here.
 *
 *  1. `utterance.lang` was never set. With it unset the platform infers the
 *     language from the TEXT, so a sentence containing Thai characters gets a
 *     Thai voice no matter which voice object we attached.
 *  2. The text itself was Mapbox's, which splices Thai road names into English
 *     sentences. That is now handled upstream in nav/phrasing.ts, but this
 *     module still refuses to speak anything carrying the wrong script - a
 *     silent instruction is better than an unintelligible one.
 *
 * Ducking: when Spotify is playing through THIS app (Web Playback SDK), we
 * lower its volume during the announcement and restore it after.
 */

import { store } from '../core/store';
import { getLang } from '../core/i18n';
import { hasThai } from '../core/lang';

export interface Ducker {
  duck(): Promise<void>;
  restore(): Promise<void>;
}

let ducker: Ducker | null = null;
export function setDucker(d: Ducker | null): void {
  ducker = d;
}

let voice: SpeechSynthesisVoice | null = null;
let voiceLang = 'en-US';
let voicesLoaded = false;

/** BCP-47 tag we ask the platform for. */
function tagFor(lang: 'en' | 'th'): string {
  return lang === 'th' ? 'th-TH' : 'en-US';
}

function pickVoice(): void {
  const all = speechSynthesis.getVoices();
  if (!all.length) return;
  voicesLoaded = true;

  const lang = getLang();
  const wanted = lang === 'th' ? 'th' : 'en';
  voiceLang = tagFor(lang);

  // Only ever consider voices for the ACTIVE language. If the device has none
  // (no Thai TTS installed, say), leave `voice` null and let the platform pick
  // using utterance.lang rather than falling back to a wrong-language voice.
  const match = all.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(wanted));
  voice =
    match.find((v) => /natural|neural|premium|enhanced/i.test(v.name)) ??
    match.find((v) => v.localService) ??
    match[0] ??
    null;

  if (voice) voiceLang = voice.lang.replace('_', '-');
}

/** Re-pick after a language switch. */
export function refreshVoice(): void {
  pickVoice();
}

if ('speechSynthesis' in window) {
  pickVoice();
  speechSynthesis.addEventListener('voiceschanged', pickVoice);
}

let speaking = false;
const queue: string[] = [];

function speakNext(): void {
  const text = queue.shift();
  if (text === undefined) return;

  const s = store.get().settings;
  const u = new SpeechSynthesisUtterance(text);
  if (!voicesLoaded) pickVoice();
  if (voice) u.voice = voice;
  // Set explicitly even when a voice is attached: some Android builds ignore
  // `voice` but honour `lang`, and with neither set they sniff the text.
  u.lang = voiceLang;
  u.volume = s.voiceVolume;
  u.rate = 1.04;

  speaking = true;
  const finish = async () => {
    speaking = false;
    if (queue.length) {
      speakNext();
    } else if (s.duckMusic) {
      await ducker?.restore().catch(() => {});
    }
  };
  u.onend = finish;
  u.onerror = finish;

  const start = () => speechSynthesis.speak(u);
  if (s.duckMusic && ducker) {
    ducker
      .duck()
      .catch(() => {})
      .finally(start);
  } else {
    start();
  }
}

/**
 * Queue an announcement. Coalesces: a newer instruction replaces queued ones.
 * Text carrying a script the active language can't read is dropped - upstream
 * should have substituted a road number.
 */
export function announce(text: string): void {
  if (!('speechSynthesis' in window)) return;
  if (!store.get().settings.voiceEnabled) return;

  const trimmed = text.trim();
  if (!trimmed) return;
  if (getLang() !== 'th' && hasThai(trimmed)) {
    console.warn('[voice] refusing to speak Thai text while language is English:', trimmed);
    return;
  }

  queue.length = 0; // stale queued instructions are worse than silence
  queue.push(trimmed);
  if (!speaking) speakNext();
}

export function stopSpeaking(): void {
  queue.length = 0;
  if ('speechSynthesis' in window) speechSynthesis.cancel();
  speaking = false;
}

/** iOS/Android unlock: call once from a user gesture so later speech works. */
export function primeSpeech(): void {
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(' ');
  u.volume = 0;
  u.lang = voiceLang;
  speechSynthesis.speak(u);
}
